import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, LogOut, Users, Plus, Edit2, Trash2, KeyRound, CheckCircle,
  Clock, XCircle, Search, Camera, X, BarChart3, TrendingUp, Award,
  Home, ChevronRight, Activity, Target, Medal, Star, ArrowUpRight, ArrowDownRight,
  Menu, XCircle as XIcon, Settings, Save, Phone, MapPin, Mail, FileText, Send,
  Dumbbell, Eye, Briefcase, User, ClipboardCheck, CalendarDays, ChevronDown, ChevronUp,
  Printer, PlusCircle, MinusCircle, FileBarChart, QrCode, RefreshCw, Bell, Building2, ToggleRight, ToggleLeft,
  Upload, History, Video
} from 'lucide-react';
import { coachService, coachAuthService, jobService } from '../services/supabaseService';
import { imageServiceProxy, storageServiceProxy, coachClubAccessServiceProxy, jobPostingServiceProxy } from '../services/proxyService';
import { useNotifications } from '../components/shared/useNotifications';
import { useConfirm } from '../components/shared/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import { DISCIPLINE_TABS } from '../utils/constants';
import imageCompression from 'browser-image-compression';
import CategoryFooter from '../components/shared/CategoryFooter';
import IrmaFloatingButton from '../components/IrmaFloatingButton';
import ClubTopbar from '../components/club/ClubTopbar';
import PoseCamera from '../components/coach/PoseCamera';
import './CoachDashboard.css';

const getDisciplineDisplay = (key) => {
  const match = DISCIPLINE_TABS.find(t => t.key === key);
  return match || { fullName: key, logo: null };
};

const CoachDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotifications();
  const confirm = useConfirm();
  const { profile, signOut, isAuthenticated, loading: authLoading, updatePassword, refreshProfile } = useAuth();

  // Helper to get coach ID from profile - profile.id es el ID de la tabla coaches (no auth_user_id)
  const getCoachId = () => profile?.id;

  // ID de auth para consultas de imágenes (tabla images usa entity_id)
  const getCoachAuthId = () => profile?.auth_user_id || profile?.id;

  const [coachInfo, setCoachInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [activePage, setActivePage] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [myClubsTab, setMyClubsTab] = useState('current'); // 'current' | 'history'
  const [employmentTab, setEmploymentTab] = useState('search'); // 'search' | 'applications'

  // Clubs
  const [myClubs, setMyClubs] = useState([]);
  const [clubHistory, setClubHistory] = useState([]);
  const [clubSearch, setClubSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestingClub, setRequestingClub] = useState(null);
  const [myRequests, setMyRequests] = useState([]);

  // Jobs
  const [jobPostings, setJobPostings] = useState([]);
  const [jobSearch, setJobSearch] = useState('');
  const [jobsLoading, setJobsLoading] = useState(false);
  const [myApplications, setMyApplications] = useState([]);
  const [applyingTo, setApplyingTo] = useState(null);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [jobDetailModal, setJobDetailModal] = useState(null); // job object for modal

  // Profile edit
  const [profileForm, setProfileForm] = useState({
    name: '', email: '', phone: '', location: '', bio: '', specialties: [], experience_years: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Coach photo
  const [coachPhotoUrl, setCoachPhotoUrl] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Coach CV
  const [cvUrl, setCvUrl] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);
  // Club images
  const [clubImageMap, setClubImageMap] = useState({});

  // Club contacts
  const [clubContacts, setClubContacts] = useState([]);
  const [clubContactsLoading, setClubContactsLoading] = useState(false);
  const [confirmingContact, setConfirmingContact] = useState(null);
  const [applicationActionLoading, setApplicationActionLoading] = useState(null);
  const [contactRequestModal, setContactRequestModal] = useState(null); // { club, applicationId }

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

useEffect(() => {
    if (authLoading) return;
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoadingTimeout(false);
    const timeout = setTimeout(() => {
      setLoadingTimeout(true);
    }, 10000);
    // El profile ya ES el coach - usar profile directamente
    setCoachInfo(profile);
    setProfileForm({
      name: profile.name || '', email: profile.email || '', phone: profile.phone || '',
      location: profile.location || '', bio: profile.bio || '',
      specialties: profile.specialties || [], experience_years: profile.experience_years || ''
    });
    if (profile.cv_url) setCvUrl(profile.cv_url);

    loadCoachExtras(profile.id, profile.email);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, [authLoading, profile?.id]);

  const loadCoachExtras = async (coachId, coachEmail) => {
    try {
      console.log('[CoachDashboard] loadCoachExtras called with coachId:', coachId);
      console.log('[CoachDashboard] profile object:', profile);
      
      // El profile.id es el ID de la tabla coaches, profile.auth_user_id es el auth user id
      const coachesTableId = profile?.id;
      const authUserId = profile?.auth_user_id || profile?.id;
      console.log('[CoachDashboard] coachesTableId:', coachesTableId, 'authUserId:', authUserId);

      // Load photo via images table - usar auth_user_id
      try {
        const { data: photoUrl, error: imgError } = await imageServiceProxy.getByEntity('coach', authUserId);
        console.log('[CoachDashboard] getByEntity result:', { photoUrl, error: imgError });
        if (photoUrl) setCoachPhotoUrl(photoUrl);
      } catch (e) { console.log('Error loading photo:', e.message); }

      // Load clubs and requests - usar coachesTableId (ID de la tabla coaches)
      console.log('[CoachDashboard] Calling getMyClubs with coachesTableId:', coachesTableId);
      const [clubsRes, requestsRes, historyRes] = await Promise.all([
        coachService.getMyClubs(coachesTableId),
        coachService.getMyRequests(coachesTableId),
        coachService.getClubHistory(coachesTableId)
      ]);
      console.log('[CoachDashboard] getMyClubs result:', clubsRes);
      console.log('[CoachDashboard] getMyRequests result:', requestsRes);
      console.log('[CoachDashboard] getClubHistory result:', historyRes);
      setMyClubs(clubsRes.data || []);
      setMyRequests(requestsRes.data || []);
      setClubHistory(historyRes.data || []);

      // Load club images (both active clubs and history clubs)
      const allClubsToLoad = [
        ...(clubsRes.data || []),
        ...((historyRes.data || []).filter(h => h.clubs).map(h => h.clubs))
      ];
      if (allClubsToLoad.length > 0) {
        const images = {};
        const seen = new Set();
        for (const club of allClubsToLoad) {
          if (!club.id || seen.has(club.id)) continue;
          seen.add(club.id);
          try {
            const { data: imgUrl } = await imageServiceProxy.getByEntity('club', club.id);
            if (imgUrl) images[club.id] = imgUrl;
          } catch (e) { /* ignore */ }
        }
        setClubImageMap(images);
      }

      setLoading(false);
    } catch (err) {
      notify.error('Error al cargar datos adicionales');
      setLoading(false);
    }
  };

  const handleLogout = async () => { await signOut(); navigate('/entrenadores'); notify.success('Sesión cerrada'); };

  // ========== Toggle "Busco un Club" ==========
  const handleToggleLooking = async () => {
    const newValue = !coachInfo.looking_for_club;
    const { data } = await coachService.toggleLookingForClub(getCoachId(), newValue);
    if (data) {
      setCoachInfo(prev => ({ ...prev, looking_for_club: newValue }));
      notify.success(newValue ? 'Ahora estás visible para clubes' : 'Ya no estás buscando club');
    }
  };

  // ========== Search Clubs ==========
  const loadClubImages = useCallback(async (clubsList) => {
    const images = {};
    for (const club of clubsList) {
      try {
        const { data: imgUrl } = await imageServiceProxy.getByEntity('club', club.id);
        if (imgUrl) {
          images[club.id] = imgUrl;
        } else {
          const { data: logoUrl } = await storageServiceProxy.getClubLogo(club.id);
          if (logoUrl) images[club.id] = logoUrl;
        }
      } catch (e) { /* ignore */ }
    }
    setClubImageMap(prev => ({ ...prev, ...images }));
  }, []);

  const handleSearchClubs = useCallback(async () => {
    setSearchLoading(true);
    const { data } = await coachService.searchClubs(clubSearch);
    setSearchResults(data || []);
    setSearchLoading(false);
    if (data && data.length > 0) loadClubImages(data);
  }, [clubSearch, loadClubImages]);

  useEffect(() => {
    if (activePage === 'search-clubs') handleSearchClubs();
  }, [activePage, handleSearchClubs]);

  const handleRequestClubAccess = async (clubId) => {
    setRequestingClub(clubId);
    const { error } = await coachService.requestClubAccess(getCoachId(), clubId);
    if (error) { notify.error(error); }
    else {
      notify.success('Solicitud enviada al club. Espera aprobación.');
      const { data } = await coachService.getMyRequests(getCoachId());
      setMyRequests(data || []);
    }
    setRequestingClub(null);
  };

  const getClubRequestStatus = (clubId) => {
    const req = myRequests.find(r => r.club_id === clubId);
    return req?.status || null;
  };

  // ========== Jobs ==========
  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    const { data } = await jobService.getActivePostings(jobSearch);
    
    // Load logos for each club
    if (data && data.length > 0) {
      for (const job of data) {
        if (job.clubs?.id) {
          const { data: imgData } = await imageServiceProxy.getByEntity('club', job.clubs.id);
          if (imgData) {
            job.clubs.logo_url = imgData;
          }
        }
      }
    }
    
    setJobPostings(data || []);
    setJobsLoading(false);
  }, [jobSearch]);

  const loadMyApplications = useCallback(async () => {
    const coachId = profile?.id;
    if (!coachId) return;
    const { data } = await jobPostingServiceProxy.getApplicationsByCoach(coachId);
    
    // Load club logos
    if (data && data.length > 0) {
      for (const app of data) {
        if (app.clubs?.id && !app.clubs.logo_url) {
          const { data: imgData } = await imageServiceProxy.getByEntity('club', app.clubs.id);
          if (imgData) {
            app.clubs.logo_url = imgData;
          }
        }
      }
    }
    
    setMyApplications(data || []);
  }, [profile]);

  const handleDeleteApplication = async (applicationId) => {
    const ok = await confirm({
      title: 'Eliminar Postulación',
      message: '¿Eliminar esta postulación? Esta acción no se puede deshacer.',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    
    setApplicationActionLoading(applicationId);
    try {
      const { error } = await jobPostingServiceProxy.deleteApplication(applicationId);
      if (error) { notify.error(error); return; }
      notify.success('Postulación eliminada');
      loadMyApplications();
    } catch (err) { notify.error('Error al eliminar'); }
    setApplicationActionLoading(null);
  };

  useEffect(() => {
    if (activePage === 'employment' || activePage === 'overview') { loadJobs(); loadMyApplications(); }
  }, [activePage, loadJobs, loadMyApplications]);

  const loadClubContacts = useCallback(async () => {
    const coachId = getCoachId();
    if (!coachId) return;
    setClubContactsLoading(true);
    try {
      const { data, error } = await coachClubAccessServiceProxy.getClubContactsForCoach(coachId);
      if (error) { console.error('Error loading club contacts:', error); return; }
      
      // Load logos for each club
      if (data && data.length > 0) {
        for (const contact of data) {
          if (contact.clubs) {
            const { data: imgData } = await imageServiceProxy.getByEntity('club', contact.clubs.id);
            if (imgData) {
              contact.clubs.logo_url = imgData;
            }
          }
        }
      }
      
      setClubContacts(data || []);
    } catch (err) { console.error('Error loading club contacts:', err); }
    setClubContactsLoading(false);
  }, [profile]);

  useEffect(() => {
    if (activePage === 'club-contacts') loadClubContacts();
  }, [activePage, loadClubContacts]);

  const handleConfirmContact = async (contactId) => {
    setConfirmingContact(contactId);
    try {
      const { data, error } = await coachClubAccessServiceProxy.confirmContact(contactId);
      if (error) { notify.error(error); return; }
      notify.success('Contacto confirmado');
      loadClubContacts();
    } catch (err) { notify.error('Error al confirmar'); }
    finally { setConfirmingContact(null); }
  };

  const handleApply = async (postingId) => {
    const { error } = await jobService.applyToPosting(postingId, getCoachId(), applicationMessage);
    if (error) { notify.error(error); }
    else {
      notify.success('Postulación enviada');
      setApplyingTo(null);
      setApplicationMessage('');
      loadMyApplications();
    }
  };

  const handleRevealContactInfo = async () => {
    if (!contactRequestModal) return;
    const { applicationId, club } = contactRequestModal;
    setApplicationActionLoading(applicationId);
    try {
      await jobPostingServiceProxy.updateApplicationStatus(applicationId, 'revealed');
      notify.success(`Datos de ${club?.name || 'club'} visibles para ti`);
      setContactRequestModal(null);
      loadMyApplications();
    } catch (err) { notify.error('Error al actualizar'); }
    setApplicationActionLoading(null);
  };

  const getApplicationStatus = (postingId) => {
    const app = myApplications.find(a => a.job_posting_id === postingId);
    return app?.status || null;
  };

  // ========== Profile ==========
  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) { notify.warning('El nombre es obligatorio'); return; }
    const coachId = getCoachId();
    if (!coachId) { notify.error('No se pudo identificar el coach. Recarga la página.'); return; }
    setSavingProfile(true);
    try {
      const { error } = await coachService.update(coachId, {
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim(),
        location: profileForm.location.trim(),
        bio: profileForm.bio.trim(),
        specialties: profileForm.specialties,
        experience_years: profileForm.experience_years ? parseInt(profileForm.experience_years) : 0
      });
      if (error) throw new Error(error);
      setCoachInfo(prev => ({ ...prev, ...profileForm, experience_years: parseInt(profileForm.experience_years) || 0 }));
      // Sincronizar el profile del AuthContext (para que el % de completitud
      // y el resto de la app vean los cambios sin necesidad de F5).
      try { await refreshProfile(); } catch (_) {}
      notify.success('Perfil actualizado');
    } catch (err) { notify.error('Error al guardar: ' + err.message); }
    finally { setSavingProfile(false); }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (!newPassword) { setPasswordError('La nueva contraseña es obligatoria'); return; }
    if (newPassword.length < 6) { setPasswordError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmNewPassword) { setPasswordError('Las contraseñas no coinciden'); return; }
    setChangingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) throw new Error(error);
      setPasswordSuccess('Contraseña actualizada correctamente');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordForm(false);
      notify.success('Contraseña actualizada');
    } catch (err) { setPasswordError(err.message || 'Error al cambiar contraseña'); }
    finally { setChangingPassword(false); }
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { notify.warning('Solo se permiten archivos PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { notify.warning('El archivo no puede superar 5MB'); return; }
    setUploadingCv(true);
    try {
      const coachId = getCoachId();
      const fileName = `coaches/cv-${coachId}-${Date.now()}.pdf`;
      const result = await storageServiceProxy.upload('gymnastics-images', fileName, file);
      if (!result.success) throw new Error(result.error);
      const newUrl = result.data?.publicUrl;
      if (newUrl) {
        await coachService.update(coachId, { cv_url: newUrl });
        setCvUrl(newUrl);
        setCoachInfo(prev => ({ ...prev, cv_url: newUrl }));
        notify.success('Currículo subido exitosamente');
      }
    } catch (err) { notify.error('Error al subir currículo: ' + err.message); }
    finally { setUploadingCv(false); e.target.value = ''; }
  };

  const handleRemoveCv = async () => {
    try {
      await coachService.update(getCoachId(), { cv_url: null });
      setCvUrl(null);
      setCoachInfo(prev => ({ ...prev, cv_url: null }));
      notify.success('Currículo eliminado');
    } catch (err) { notify.error('Error al eliminar currículo'); }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.15, maxWidthOrHeight: 400, useWebWorker: true });
      setPhotoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch (err) { notify.error('Error al procesar la imagen'); }
  };

  const handleUploadPhoto = async () => {
    if (!photoFile) return;
    setUploadingPhoto(true);
    try {
      const coachId = getCoachId();
      const authId = getCoachAuthId();
      const fileExt = photoFile.name.split('.').pop();
      const fileName = `coaches/${coachId}-${Date.now()}.${fileExt}`;
      const result = await storageServiceProxy.upload('gymnastics-images', fileName, photoFile);
      if (!result.success) throw new Error(result.error);
      if (result.data?.publicUrl) {
        const photoUrl = result.data.publicUrl;
        setCoachPhotoUrl(photoUrl);
        setPhotoPreview(null);
        setPhotoFile(null);
        // Guardar en tabla images - usar authUserId para entity_id
        const imgResult = await imageServiceProxy.setImage('coach', authId, photoUrl);
        // imgResult.data puede ser undefined pero si no hay error, fue exitoso
        if (imgResult.error) {
          console.error('Error guardando imagen:', imgResult.error);
          notify.error('Error al guardar la foto');
        } else {
          console.log('Imagen guardada exitosamente');
          notify.success('Foto de perfil actualizada');
        }
        return;
      }
      notify.success('Foto de perfil actualizada');
    } catch (err) { notify.error('Error al subir foto: ' + err.message); }
    finally { setUploadingPhoto(false); }
  };

  const toggleSpecialty = (key) => {
    setProfileForm(prev => ({
      ...prev,
      specialties: prev.specialties.includes(key) ? prev.specialties.filter(d => d !== key) : [...prev.specialties, key]
    }));
  };

  // ========== Helpers ==========
  const getInitials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="cch-badge cch-badge-green"><CheckCircle size={12} /> Aprobado</span>;
      case 'pending': return <span className="cch-badge cch-badge-yellow"><Clock size={12} /> Esperando Respuesta</span>;
      case 'rejected': return <span className="cch-badge cch-badge-red"><XCircle size={12} /> Rechazado</span>;
      default: return <span className="cch-badge">{status}</span>;
    }
  };

  // ============ Acceso al módulo de Análisis de Pose ============
  // Disponible si AL MENOS UN club aprobado del entrenador cumple AMBOS:
  //   - clubs.pose_analysis_enabled = true (switch maestro del club)
  //   - coach_club_access.pose_analysis_enabled = true (el club habilitó
  //     explícitamente a este entrenador en la pestaña "Autorizados").
  const poseAnalysisEnabledClubs = (myClubs || []).filter(
    c => c?.pose_analysis_enabled === true && c?.access_pose_analysis_enabled === true
  );
  const hasPoseAccess = poseAnalysisEnabledClubs.length > 0;

  // Si el entrenador pierde el acceso mientras estaba en esa página → redirigir
  useEffect(() => {
    if (activePage === 'pose-analysis' && !hasPoseAccess) {
      setActivePage('overview');
    }
  }, [activePage, hasPoseAccess]);

  // Nav items
  const navItems = [
    { id: 'overview', label: 'Resumen', icon: Home, color: '#3b82f6' },
    { id: 'my-clubs', label: 'Mis Clubes', icon: Building2, color: '#10b981' },
    { id: 'search-clubs', label: 'Buscar Clubes', icon: Search, color: '#8b5cf6' },
    { id: 'employment', label: 'Empleo', icon: Briefcase, color: '#f59e0b' },
    { id: 'club-contacts', label: 'Contactos', icon: Bell, color: '#ef4444' },
    ...(hasPoseAccess
      ? [{ id: 'pose-analysis', label: 'Análisis de Pose', icon: Video, color: '#0ea5e9' }]
      : []),
  ];

  if (loading) {
    return (
      <div className="cch-loading">
        <div className="cch-spinner"></div>
        <p>Cargando panel del entrenador...</p>
      </div>
    );
  }

  if (!coachInfo) {
    if (loadingTimeout) {
      return (
        <div className="cch-loading">
          <p>No se pudo cargar la información</p>
          <button onClick={() => window.location.reload()} className="btn btn-coach-primary" style={{ marginTop: 16 }}>Recargar</button>
        </div>
      );
    }
    return (
      <div className="cch-loading">
        <div className="cch-spinner"></div>
        <p>Cargando panel del entrenador...</p>
      </div>
    );
  }

  return (
    <div className="cch-layout">
      {/* SIDEBAR TOGGLE */}
      <button className="cch-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
      {sidebarOpen && <div className="cch-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* SIDEBAR */}
      <aside className={`cch-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="cch-sidebar-brand">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="cch-sidebar-logo-img" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </div>

        {/* Busco un Club Toggle */}
        <div className="cch-looking-toggle" onClick={handleToggleLooking}>
          {coachInfo.looking_for_club ? <ToggleRight size={24} className="cch-toggle-on" /> : <ToggleLeft size={24} className="cch-toggle-off" />}
          <span className={coachInfo.looking_for_club ? 'cch-toggle-text-on' : 'cch-toggle-text-off'}>
            Busco un Club
          </span>
        </div>

        <nav className="cch-sidebar-nav">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activePage === item.id;
            const badge = item.id === 'my-clubs' ? myClubs.length : item.id === 'applications' ? myApplications.filter(a => a.status === 'pending').length : null;
            return (
              <button
                key={item.id}
                className={`cch-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => { setActivePage(item.id); setSidebarOpen(false); }}
                style={{ '--item-color': item.color }}
              >
                <span className="cch-nav-icon"><Icon size={20} /></span>
                <span className="cch-nav-label">{item.label}</span>
                {badge > 0 && <span className="cch-nav-badge">{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="cch-sidebar-footer">
          <button className="cch-sidebar-logout" onClick={handleLogout}>
            <LogOut size={18} /><span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="cch-main">
        <ClubTopbar
          pageTitle={navItems.find(n => n.id === activePage)?.label || (activePage === 'profile' ? 'Mi Perfil' : 'Panel')}
          onToggleSidebar={() => setSidebarOpen(o => !o)}
          clubName={coachInfo.name || ''}
          clubLogoUrl={coachPhotoUrl || null}
          initials={getInitials(coachInfo.name)}
          role="Entrenador/a"
          roleLabel="Cuenta de entrenador/a"
          showSettings={false}
          onGoProfile={() => { setActivePage('profile'); setSidebarOpen(false); }}
          onLogout={handleLogout}
          notificationsCount={myApplications.filter(a => a.status === 'pending').length}
        />

        <div className="cch-page">

          {/* ============ OVERVIEW ============ */}
          {activePage === 'overview' && (() => {
            const hour = new Date().getHours();
            const greeting = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
            const firstName = (coachInfo.name || '').split(' ')[0] || '';

            // Completitud del perfil
            const checks = [
              { key: 'photo', label: 'Sube tu foto de perfil', done: !!coachPhotoUrl },
              { key: 'bio', label: 'Escribe una bio profesional', done: !!(coachInfo.bio && coachInfo.bio.trim().length >= 10) },
              { key: 'specialties', label: 'Añade al menos una disciplina', done: (coachInfo.specialties || []).length > 0 },
              { key: 'cv', label: 'Carga tu currículo en PDF', done: !!cvUrl },
              { key: 'phone', label: 'Añade un teléfono de contacto', done: !!coachInfo.phone },
              { key: 'experience', label: 'Indica tus años de experiencia', done: !!coachInfo.experience_years },
            ];
            const doneCount = checks.filter(c => c.done).length;
            const pct = Math.round((doneCount / checks.length) * 100);
            const missing = checks.filter(c => !c.done);

            // Recomendaciones inteligentes: vacantes que matchean especialidades
            const mySpecs = new Set(coachInfo.specialties || []);
            const myClubIds = new Set(myClubs.map(c => c.id));
            const appliedJobIds = new Set(myApplications.map(a => a.job_posting_id || a.id));
            const recommendedJobs = (jobPostings || [])
              .filter(j => !appliedJobIds.has(j.id) && !myClubIds.has(j.clubs?.id))
              .map(j => ({
                ...j,
                _matchScore: (j.disciplines || []).filter(d => mySpecs.has(d)).length,
              }))
              .sort((a, b) => b._matchScore - a._matchScore)
              .slice(0, 3);

            const pendingContacts = (clubContacts || []).filter(c => c.status === 'pending').length;
            const pendingRequests = (myRequests || []).filter(r => r.status === 'pending').length;
            const activeApps = (myApplications || []).filter(a => a.status === 'pending').length;

            return (
            <>
              {/* ====== HERO de bienvenida ====== */}
              <div style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 55%, #1e1b4b 100%)',
                borderRadius: 20,
                padding: '28px 28px 24px',
                color: 'white',
                overflow: 'hidden',
                boxShadow: '0 20px 40px -20px rgba(76, 29, 149, 0.55)',
                marginBottom: 20,
              }}>
                {/* Decorativo */}
                <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(251,191,36,0.25), transparent 70%)' }} />
                <div style={{ position: 'absolute', bottom: -40, left: -20, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(236,72,153,0.18), transparent 70%)' }} />

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{
                    width: 86, height: 86, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)',
                    border: '3px solid rgba(255,255,255,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0, fontSize: '2rem', fontWeight: 800,
                  }}>
                    {coachPhotoUrl ? (
                      <img src={coachPhotoUrl} alt={coachInfo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : getInitials(coachInfo.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ margin: 0, fontSize: '0.82rem', opacity: 0.75, letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>
                      {greeting},
                    </p>
                    <h1 style={{ margin: '2px 0 6px', fontSize: '1.85rem', fontWeight: 800, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ display: 'inline' }}>{firstName}</span>{' '}
                      <span style={{ display: 'inline-block' }}>👋</span>
                    </h1>
                    <p style={{ margin: 0, fontSize: '0.92rem', opacity: 0.9 }}>
                      {myClubs.length > 0
                        ? `Entrenas en ${myClubs.length} ${myClubs.length === 1 ? 'club' : 'clubes'} · ${(coachInfo.specialties || []).length} ${(coachInfo.specialties || []).length === 1 ? 'disciplina' : 'disciplinas'}`
                        : 'Empieza tu viaje: busca clubes y postula a vacantes.'}
                    </p>
                  </div>

                  {/* Mini-stats inline */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', padding: '10px 14px', borderRadius: 12, minWidth: 90, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1 }}>{coachInfo.experience_years || 0}</div>
                      <div style={{ fontSize: '0.7rem', opacity: 0.85, marginTop: 4 }}>años exp.</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', padding: '10px 14px', borderRadius: 12, minWidth: 90, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1 }}>{activeApps}</div>
                      <div style={{ fontSize: '0.7rem', opacity: 0.85, marginTop: 4 }}>post. activas</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', padding: '10px 14px', borderRadius: 12, minWidth: 90, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, color: pendingContacts > 0 ? '#fbbf24' : 'white' }}>{pendingContacts}</div>
                      <div style={{ fontSize: '0.7rem', opacity: 0.85, marginTop: 4 }}>contactos</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ====== Accesos rápidos ====== */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 12,
                marginBottom: 20,
              }}>
                {[
                  { id: 'search-clubs', label: 'Buscar clubes', desc: 'Encuentra nuevos equipos', icon: Search, color: '#8b5cf6', bg: '#f5f3ff' },
                  { id: 'employment', label: 'Ver vacantes', desc: `${jobPostings.length} ofertas activas`, icon: Briefcase, color: '#f59e0b', bg: '#fffbeb' },
                  ...(hasPoseAccess
                    ? [{ id: 'pose-analysis', label: 'Análisis de Pose', desc: 'Graba con IA', icon: Video, color: '#0ea5e9', bg: '#f0f9ff' }]
                    : []),
                  { id: 'profile', label: 'Editar perfil', desc: `${pct}% completo`, icon: User, color: '#10b981', bg: '#ecfdf5' },
                ].map(a => {
                  const Icon = a.icon;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setActivePage(a.id)}
                      style={{
                        background: 'white', border: '1px solid #e4e4e7', borderRadius: 14,
                        padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = a.color; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e4e4e7'; }}
                    >
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: a.bg, color: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={22} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#18181b' }}>{a.label}</div>
                        <div style={{ fontSize: '0.76rem', color: '#71717a', marginTop: 2 }}>{a.desc}</div>
                      </div>
                      <ChevronRight size={16} style={{ color: '#a1a1aa', flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>

              {/* ====== Completitud del perfil (solo si falta algo) ====== */}
              {pct < 100 && (
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
                  border: '1px solid #fde68a', borderRadius: 14, padding: 18, marginBottom: 20,
                  display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap',
                }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
                    <Star size={26} style={{ color: '#f59e0b' }} fill="#fbbf24" />
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '1rem', color: '#78350f' }}>Completa tu perfil ({pct}%)</strong>
                      <span style={{ fontSize: '0.8rem', color: '#92400e' }}>{doneCount}/{checks.length} listo</span>
                    </div>
                    <div style={{ height: 8, background: 'rgba(255,255,255,0.6)', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #ef4444)', transition: 'width 0.6s', borderRadius: 10 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {missing.slice(0, 4).map(m => (
                        <button key={m.key} onClick={() => setActivePage('profile')} style={{
                          background: 'white', border: '1px solid #fde68a', borderRadius: 100,
                          padding: '5px 12px', fontSize: '0.75rem', color: '#92400e', fontWeight: 600,
                          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                          <PlusCircle size={12} /> {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ====== Recomendaciones inteligentes ====== */}
              {recommendedJobs.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        <TrendingUp size={18} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#18181b' }}>Recomendado para ti</h3>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#71717a' }}>Vacantes que coinciden con tu perfil</p>
                      </div>
                    </div>
                    <button onClick={() => setActivePage('employment')} style={{
                      background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600,
                      fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                      Ver todas <ChevronRight size={14} />
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                    {recommendedJobs.map(job => (
                      <button key={job.id} onClick={() => setJobDetailModal(job)} style={{
                        background: 'white', border: '1px solid #e4e4e7', borderRadius: 14,
                        padding: 16, cursor: 'pointer', textAlign: 'left',
                        display: 'flex', flexDirection: 'column', gap: 10,
                        transition: 'all 0.15s', position: 'relative', overflow: 'hidden',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e4e4e7'; }}
                      >
                        {job._matchScore > 0 && (
                          <span style={{ position: 'absolute', top: 10, right: 10, background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', fontSize: '0.68rem', padding: '3px 8px', borderRadius: 100, fontWeight: 700 }}>
                            ⭐ {job._matchScore} match
                          </span>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, overflow: 'hidden', background: '#f4f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {job.clubs?.logo_url
                              ? <img src={job.clubs.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <Building2 size={18} color="#94a3b8" />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#18181b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.title}</div>
                            <div style={{ fontSize: '0.78rem', color: '#71717a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.clubs?.name || 'Club'}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, fontSize: '0.75rem', color: '#71717a', flexWrap: 'wrap' }}>
                          {job.location && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin size={12} /> {job.location}</span>}
                          {job.salary_range && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Award size={12} /> {job.salary_range}</span>}
                        </div>
                        {job.disciplines && job.disciplines.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {job.disciplines.slice(0, 3).map((dk, i) => {
                              const d = getDisciplineDisplay(dk);
                              const isMatch = mySpecs.has(dk);
                              return (
                                <span key={i} style={{
                                  fontSize: '0.7rem', padding: '3px 8px', borderRadius: 100,
                                  background: isMatch ? '#d1fae5' : '#f4f4f5',
                                  color: isMatch ? '#065f46' : '#52525b',
                                  fontWeight: 600,
                                }}>
                                  {d.fullName}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ====== Grid: Mis Clubes + Alertas ====== */}
              <div className="cch-overview-grid">
                <div className="cch-card">
                  <div className="cch-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Building2 size={16} /> Mis Clubes</span>
                    {myClubs.length > 0 && (
                      <button onClick={() => setActivePage('my-clubs')} style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        Ver todos <ChevronRight size={12} />
                      </button>
                    )}
                  </div>
                  {myClubs.length > 0 ? myClubs.slice(0, 4).map(club => (
                    <div key={club.id} className="cch-list-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1.5px solid #e4e4e7', flexShrink: 0 }}>
                          {clubImageMap[club.id]
                            ? <img src={clubImageMap[club.id]} alt={club.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Building2 size={18} style={{ color: '#a1a1aa' }} />}
                        </div>
                        <div>
                          <strong>{club.name}</strong>
                          <span>{club.location || 'Sin ubicación'}</span>
                        </div>
                      </div>
                      <span className="cch-badge cch-badge-green">{club.role || 'Entrenador'}</span>
                    </div>
                  )) : (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Building2 size={32} style={{ color: '#d4d4d8', margin: '0 auto 8px' }} />
                      <p className="cch-empty-text" style={{ marginBottom: 10 }}>Aún no formas parte de ningún club</p>
                      <button onClick={() => setActivePage('search-clubs')} style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Search size={14} /> Buscar clubes
                      </button>
                    </div>
                  )}
                </div>

                <div className="cch-card">
                  <div className="cch-card-header"><Bell size={16} /> Actividad reciente</div>
                  {pendingContacts === 0 && pendingRequests === 0 && activeApps === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <CheckCircle size={32} style={{ color: '#10b981', margin: '0 auto 8px' }} />
                      <p className="cch-empty-text">¡Al día! No hay notificaciones.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {pendingContacts > 0 && (
                        <button onClick={() => setActivePage('club-contacts')} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Bell size={18} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '0.88rem', color: '#78350f' }}>{pendingContacts} club{pendingContacts > 1 ? 'es quieren' : ' quiere'} contactarte</strong>
                            <div style={{ fontSize: '0.75rem', color: '#92400e' }}>Revisa tus contactos</div>
                          </div>
                          <ChevronRight size={16} style={{ color: '#92400e' }} />
                        </button>
                      )}
                      {pendingRequests > 0 && (
                        <button onClick={() => setActivePage('my-clubs')} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#3b82f6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Clock size={18} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '0.88rem', color: '#1e3a8a' }}>{pendingRequests} solicitud{pendingRequests > 1 ? 'es' : ''} a clubes</strong>
                            <div style={{ fontSize: '0.75rem', color: '#1e40af' }}>Esperando respuesta</div>
                          </div>
                          <ChevronRight size={16} style={{ color: '#1e40af' }} />
                        </button>
                      )}
                      {activeApps > 0 && (
                        <button onClick={() => { setActivePage('employment'); setEmploymentTab('applications'); }} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 10, padding: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ec4899', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Send size={18} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '0.88rem', color: '#831843' }}>{activeApps} postulación{activeApps > 1 ? 'es' : ''} activa{activeApps > 1 ? 's' : ''}</strong>
                            <div style={{ fontSize: '0.75rem', color: '#9f1239' }}>Revisa el estado</div>
                          </div>
                          <ChevronRight size={16} style={{ color: '#9f1239' }} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
            );
          })()}

          {/* ============ PROFILE ============ */}
          {activePage === 'profile' && (
            <>
              <p className="cch-page-subtitle"><Settings size={16} />Actualiza tus datos personales, experiencia, disciplinas y foto de perfil.</p>

              <div className="cch-profile-grid">

                {/* ---- Columna Izquierda: Foto + Datos personales ---- */}
                <div className="cch-card">
                  <div className="cch-card-header"><Settings size={16} /> Datos personales</div>

                  <div className="cch-photo-upload-section">
                    <div className="cch-photo-wrapper">
                      <div className="cch-photo-circle">
                        {(photoPreview || coachPhotoUrl) ? (
                          <img src={photoPreview || coachPhotoUrl} alt="Foto" />
                        ) : (
                          <span className="cch-photo-initials">{getInitials(profileForm.name)}</span>
                        )}
                      </div>
                      <label className="cch-photo-camera-btn" title="Cambiar foto">
                        <Camera size={16} />
                        <input type="file" accept="image/*" onChange={handlePhotoChange} hidden />
                      </label>
                    </div>
                    <div className="cch-photo-info">
                      <span className="cch-photo-name">{profileForm.name || 'Tu nombre'}</span>
                      <span className="cch-photo-hint">JPG, PNG — máx. 2MB</span>
                      {photoPreview && (
                        <div className="cch-photo-btns">
                          <button className="cch-cv-btn cch-cv-btn-view" onClick={handleUploadPhoto} disabled={uploadingPhoto}>
                            {uploadingPhoto ? 'Subiendo...' : 'Guardar Foto'}
                          </button>
                          <button className="cch-cv-btn cch-cv-btn-delete" onClick={() => { setPhotoPreview(null); setPhotoFile(null); }}>
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="cch-form-group">
                    <label>Nombre Completo</label>
                    <input type="text" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="cch-form-group">
                    <label>Correo Electrónico</label>
                    <input type="email" value={profileForm.email} disabled className="cch-input-disabled" />
                  </div>
                  <div className="cch-form-grid">
                    <div className="cch-form-group">
                      <label>Teléfono</label>
                      <input type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div className="cch-form-group">
                      <label>Años de Experiencia</label>
                      <input type="number" value={profileForm.experience_years} onChange={e => setProfileForm(p => ({ ...p, experience_years: e.target.value }))} min="0" max="60" />
                    </div>
                  </div>
                  <div className="cch-form-group">
                    <label>Ubicación</label>
                    <input type="text" value={profileForm.location} onChange={e => setProfileForm(p => ({ ...p, location: e.target.value }))} />
                  </div>
                </div>

                {/* ---- Columna Derecha: Bio + Disciplinas + CV ---- */}
                <div className="cch-card">
                  <div className="cch-card-header"><FileText size={16} /> Perfil profesional</div>

                  <div className="cch-form-group">
                    <label>Sobre mí</label>
                    <textarea value={profileForm.bio} onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))} rows={4} className="cch-textarea" placeholder="Cuéntanos tu trayectoria, logros y estilo como entrenador/a..." />
                  </div>

                  <div className="cch-form-group" style={{ marginTop: 16 }}>
                    <label>Especialidades / Disciplinas</label>
                    <div className="cch-discipline-grid">
                      {DISCIPLINE_TABS.map(d => (
                        <button key={d.key} type="button" className={`cch-discipline-chip ${profileForm.specialties.includes(d.key) ? 'active' : ''}`} onClick={() => toggleSpecialty(d.key)}>
                          {d.logo && <img src={d.logo} alt={d.fullName} style={{ width: 22, height: 22, objectFit: 'contain' }} />}
                          <span>{d.fullName}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="cch-form-group" style={{ marginTop: 20 }}>
                    <label><FileText size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Currículo (PDF)</label>
                    <div className="cch-cv-upload-area">
                      {cvUrl ? (
                        <div className="cch-cv-current">
                          <div className="cch-cv-icon"><FileText size={24} /></div>
                          <div className="cch-cv-info">
                            <span className="cch-cv-label">Currículo cargado</span>
                            <div className="cch-cv-actions">
                              <a href={cvUrl} target="_blank" rel="noopener noreferrer" className="cch-cv-btn cch-cv-btn-view">
                                <Eye size={14} /> Ver PDF
                              </a>
                              <button className="cch-cv-btn cch-cv-btn-delete" onClick={handleRemoveCv}>
                                <Trash2 size={14} /> Eliminar
                              </button>
                              <label className="cch-cv-btn cch-cv-btn-replace">
                                <Upload size={14} /> Reemplazar
                                <input type="file" accept="application/pdf" onChange={handleCvUpload} hidden />
                              </label>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <label className="cch-cv-dropzone">
                          <Upload size={28} />
                          <span>{uploadingCv ? 'Subiendo...' : 'Subir Currículo en PDF'}</span>
                          <span className="cch-cv-hint">Máximo 5MB</span>
                          <input type="file" accept="application/pdf" onChange={handleCvUpload} hidden disabled={uploadingCv} />
                        </label>
                      )}
                    </div>
                  </div>

                  <button className="btn btn-coach-primary" onClick={handleSaveProfile} disabled={savingProfile} style={{ marginTop: 20, width: '100%' }}>
                    {savingProfile ? 'Guardando...' : 'Guardar Perfil'}
                  </button>
                </div>

              </div>

              {/* ---- Seguridad (ancho completo) ---- */}
              <div className="cch-card" style={{ marginTop: 16 }}>
                <div className="cch-card-header"><KeyRound size={16} /> Seguridad</div>

                {!showPasswordForm ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 16,
                  }}>
                    <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                      <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.95rem', color: '#18181b' }}>
                        Contraseña
                      </p>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#71717a' }}>
                        Cámbiala regularmente para mantener tu cuenta protegida.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowPasswordForm(true)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 18px',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(217, 119, 6, 0.25)',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(217, 119, 6, 0.35)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 119, 6, 0.25)'; }}
                    >
                      <KeyRound size={16} /> Cambiar Contraseña
                    </button>
                  </div>
                ) : (
                  <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>Nueva Contraseña</h4>
                    <div className="cch-form-grid">
                      <div className="cch-form-group">
                        <label>Nueva Contraseña</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
                      </div>
                      <div className="cch-form-group">
                        <label>Confirmar Contraseña</label>
                        <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="Repite la contraseña" />
                      </div>
                    </div>
                    {passwordError && <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '8px 0' }}>{passwordError}</p>}
                    {passwordSuccess && <p style={{ color: '#059669', fontSize: '0.85rem', margin: '8px 0' }}>{passwordSuccess}</p>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      <button className="btn btn-coach-primary" onClick={handleChangePassword} disabled={changingPassword}>
                        {changingPassword ? 'Cambiando...' : 'Confirmar'}
                      </button>
                      <button className="btn btn-coach-secondary" onClick={() => { setShowPasswordForm(false); setNewPassword(''); setConfirmNewPassword(''); setPasswordError(''); }}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ============ MY CLUBS ============ */}
          {activePage === 'my-clubs' && (
            <>
              <p className="cch-page-subtitle"><Building2 size={16} />Clubes donde trabajas actualmente. Gestiona tu afiliación y contacto con cada uno.</p>

              {/* Tabs — modern underlined */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {[
                  { id: 'current', label: `Actuales (${myClubs.length})`, icon: Building2 },
                  { id: 'history', label: `Historial (${clubHistory.length})`, icon: Clock },
                ].map(tab => {
                  const isActive = myClubsTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setMyClubsTab(tab.id)}
                      style={{
                        background: 'transparent',
                        color: isActive ? '#8b5cf6' : '#64748b',
                        border: 'none',
                        padding: '10px 16px',
                        marginBottom: -1,
                        borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
                    >
                      <tab.icon size={15} /> {tab.label}
                    </button>
                  );
                })}
              </div>

            <div className="cch-card">
              
              {myClubsTab === 'current' && (
                myClubs.length > 0 ? (
                  <div className="cch-clubs-grid">
                    {myClubs.map(club => (
                      <div key={club.id} className="cch-club-card">
                        <div className="cch-club-avatar">
                          {clubImageMap[club.id] ? <img src={clubImageMap[club.id]} alt={club.name} /> : getInitials(club.name)}
                        </div>
                        <div className="cch-club-info">
                          <h3>{club.name}</h3>
                          {club.location && <p><MapPin size={14} /> {club.location}</p>}
                          {club.email && <p><Mail size={14} /> {club.email}</p>}
                          {(club.facebook_url || club.instagram_url || club.twitter_url) && (
                            <div className="cch-club-socials">
                              {club.facebook_url && (
                                <a href={club.facebook_url} target="_blank" rel="noopener noreferrer" className="cch-social-icon cch-social-fb" title="Facebook">
                                  <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V330.1h-58.5v-74h58.5v-56.4c0-57.7 34.4-89.6 87-89.6 25.2 0 51.5 4.5 51.5 4.5v56.6h-29c-28.5 0-37.4 17.7-37.4 35.8V256h63.9l-10.2 74h-53.7v174.4C429.3 476.8 512 376 512 256z"/></svg>
                                </a>
                              )}
                              {club.instagram_url && (
                                <a href={club.instagram_url} target="_blank" rel="noopener noreferrer" className="cch-social-icon cch-social-ig" title="Instagram">
                                  <svg width="14" height="14" viewBox="0 0 448 512" fill="currentColor"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9S160.5 370.9 224.1 370.9 339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1S3.8 127.9 2 163.8c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7 2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>
                                </a>
                              )}
                              {club.twitter_url && (
                                <a href={club.twitter_url} target="_blank" rel="noopener noreferrer" className="cch-social-icon cch-social-x" title="X (Twitter)">
                                  <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8l164.9-188.5L26.8 48h145.6l100.5 132.9zm-24.8 373.8h39.1L151.1 88h-42z"/></svg>
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="cch-badge cch-badge-green">{club.role || 'Entrenador'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="cch-empty-state">
                    <Building2 size={48} />
                    <h3>Sin clubes asociados</h3>
                    <p>Busca un club y solicita acceso para empezar.</p>
                    <button className="cch-btn-primary" onClick={() => setActivePage('search-clubs')}>Buscar Clubes</button>
                  </div>
                )
              )}
              
              {myClubsTab === 'history' && (
                clubHistory.length > 0 ? (
                  <div className="cch-history-list">
                    {clubHistory.map(item => (
                      <div key={item.id} className="cch-history-row">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div className="cch-history-avatar">
                            {clubImageMap[item.clubs?.id]
                              ? <img src={clubImageMap[item.clubs.id]} alt={item.clubs?.name} />
                              : <Building2 size={18} style={{ color: '#a1a1aa' }} />}
                          </div>
                          <div className="cch-history-info">
                            <strong>{item.clubs?.name || 'Club'}</strong>
                            <span>{item.clubs?.location || ''}</span>
                          </div>
                        </div>
                        <div className="cch-history-meta">
                          <span className="cch-history-date">{new Date(item.requested_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          {renderStatusBadge(item.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="cch-empty-state">
                    <History size={48} />
                    <h3>Sin historial</h3>
                    <p>Aquí aparecerá tu historial de clubes.</p>
                  </div>
                )
              )}
            </div>
            </>
          )}

          {/* ============ SEARCH CLUBS ============ */}
          {activePage === 'search-clubs' && (
            <>
              <p className="cch-page-subtitle"><Search size={16} />Explora clubes disponibles y postula para trabajar como entrenador/a.</p>
            <div className="cch-card">
              <div className="cch-card-header"><Search size={16} /> Directorio de clubes</div>
              <div className="cch-search-bar">
                <Search size={18} />
                <input type="text" placeholder="Buscar por nombre o ubicación..." value={clubSearch} onChange={e => setClubSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearchClubs()} />
                <button className="cch-btn-primary" onClick={handleSearchClubs} disabled={searchLoading}>
                  {searchLoading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>

              <div className="cch-clubs-grid">
                {searchResults.map(club => {
                  const status = getClubRequestStatus(club.id);
                  const isMyClub = myClubs.some(c => c.id === club.id);
                  return (
                    <div key={club.id} className="cch-club-card">
                      <div className="cch-club-avatar">
                        {clubImageMap[club.id] ? <img src={clubImageMap[club.id]} alt={club.name} /> : getInitials(club.name)}
                      </div>
                      <div className="cch-club-info">
                        <h3>{club.name}</h3>
                        {club.location && <p><MapPin size={14} /> {club.location}</p>}
                        {(club.facebook_url || club.instagram_url || club.twitter_url) && (
                          <div className="cch-club-socials">
                            {club.facebook_url && (
                              <a href={club.facebook_url} target="_blank" rel="noopener noreferrer" className="cch-social-icon cch-social-fb" title="Facebook">
                                <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V330.1h-58.5v-74h58.5v-56.4c0-57.7 34.4-89.6 87-89.6 25.2 0 51.5 4.5 51.5 4.5v56.6h-29c-28.5 0-37.4 17.7-37.4 35.8V256h63.9l-10.2 74h-53.7v174.4C429.3 476.8 512 376 512 256z"/></svg>
                              </a>
                            )}
                            {club.instagram_url && (
                              <a href={club.instagram_url} target="_blank" rel="noopener noreferrer" className="cch-social-icon cch-social-ig" title="Instagram">
                                <svg width="14" height="14" viewBox="0 0 448 512" fill="currentColor"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9S160.5 370.9 224.1 370.9 339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1S3.8 127.9 2 163.8c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>
                              </a>
                            )}
                            {club.twitter_url && (
                              <a href={club.twitter_url} target="_blank" rel="noopener noreferrer" className="cch-social-icon cch-social-x" title="X (Twitter)">
                                <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8l164.9-188.5L26.8 48h145.6l100.5 132.9zm-24.8 373.8h39.1L151.1 88h-42z"/></svg>
                              </a>
                            )}
                          </div>
                        )}
                        {club.disciplines && club.disciplines.length > 0 && (
                          <div className="cch-club-disciplines">
                            {club.disciplines.slice(0, 6).map(dk => {
                              const d = getDisciplineDisplay(dk);
                              return (
                                <span key={dk} className="cch-discipline-logo-tag" title={d.fullName}>
                                  {d.logo && <img src={d.logo} alt={d.name || dk} />}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="cch-club-action">
                        {isMyClub ? (
                          <span className="cch-badge cch-badge-green"><CheckCircle size={12} /> Miembro</span>
                        ) : status === 'pending' ? (
                          <span className="cch-badge cch-badge-yellow"><Clock size={12} /> Esperando Respuesta</span>
                        ) : status === 'rejected' ? (
                          <span className="cch-badge cch-badge-red"><XCircle size={12} /> Rechazado</span>
                        ) : (
                          <button className="cch-btn-sm" onClick={() => handleRequestClubAccess(club.id)} disabled={requestingClub === club.id}>
                            {requestingClub === club.id ? 'Enviando...' : 'Solicitar Acceso'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {searchResults.length === 0 && !searchLoading && (
                  <p className="cch-empty-text" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 40 }}>
                    Busca clubes para solicitar acceso
                  </p>
                )}
              </div>
            </div>
            </>
          )}

{/* ============ EMPLEMENT ============ */}
          {activePage === 'employment' && (
            <>
              <p className="cch-page-subtitle"><Briefcase size={16} />Explora ofertas de trabajo publicadas por clubes y gestiona tus postulaciones.</p>

              {/* Tabs — modern underlined */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {[
                  { id: 'search', label: `Buscar Vacantes (${jobPostings.length})`, icon: Search },
                  { id: 'applications', label: `Mis Postulaciones (${myApplications.length})`, icon: Send },
                ].map(tab => {
                  const isActive = employmentTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setEmploymentTab(tab.id)}
                      style={{
                        background: 'transparent',
                        color: isActive ? '#8b5cf6' : '#64748b',
                        border: 'none',
                        padding: '10px 16px',
                        marginBottom: -1,
                        borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
                    >
                      <tab.icon size={15} /> {tab.label}
                    </button>
                  );
                })}
              </div>

            <div className="cch-card">
              {employmentTab === 'search' && (
                <>
                  <div className="cch-search-bar">
                    <Search size={18} />
                    <input type="text" placeholder="Buscar ofertas..." value={jobSearch} onChange={e => setJobSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadJobs()} />
                    <button className="btn btn-coach-primary" onClick={loadJobs} disabled={jobsLoading}>
                      {jobsLoading ? 'Buscando...' : 'Buscar'}
                    </button>
                  </div>

                  <div className="cch-jobs-grid">
                    {jobPostings.map(job => {
                      const appStatus = getApplicationStatus(job.id);
                      return (
                        <div key={job.id} className="cch-job-card">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, overflow: 'hidden', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {job.clubs?.logo_url ? (
                                <img src={job.clubs.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Building2 size={20} color="#94a3b8" />
                              )}
                            </div>
                            <div>
                              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>{job.title}</h3>
                              <span className="cch-job-club">{job.clubs?.name || 'Club'}</span>
                            </div>
                          </div>
                          {job.description && <p className="cch-job-desc">{job.description}</p>}
                          <div className="cch-job-meta">
                            {job.location && <span><MapPin size={14} /> {job.location}</span>}
                            {job.salary_range && <span><Award size={14} /> {job.salary_range}</span>}
                          </div>
                          {job.disciplines && job.disciplines.length > 0 && (
                            <div className="cch-job-tags">
                              {job.disciplines.map((dk, i) => {
                                const d = getDisciplineDisplay(dk);
                                return (
                                  <span key={i} className="cch-job-tag">
                                    {d.logo && <img src={d.logo} alt="" style={{ width: 12, height: 12 }} />}
                                    {d.fullName}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button 
                              className="cch-btn-sm" 
                              style={{ background: '#f1f5f9', color: '#475569' }}
                              onClick={() => setJobDetailModal(job)}
                            >
                              <Eye size={14} /> Ver Detalles
                            </button>
                          </div>
                          <div className="cch-job-footer">
                            {appStatus === 'pending' ? (
                              <span className="cch-badge cch-badge-yellow"><Clock size={12} /> Postulado</span>
                            ) : appStatus === 'accepted' ? (
                              <span className="cch-badge cch-badge-green"><CheckCircle size={12} /> Aceptado</span>
                            ) : appStatus === 'rejected' ? (
                              <span className="cch-badge cch-badge-red"><XCircle size={12} /> Rechazado</span>
                            ) : (
                              <button className="btn btn-coach-primary" onClick={() => setApplyingTo(job.id)}>
                                <Send size={14} /> Postularme
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {jobPostings.length === 0 && !jobsLoading && (
                      <p style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>
                        No hay ofertas de empleo disponibles actualmente
                      </p>
                    )}
                  </div>
                </>
              )}

              {employmentTab === 'applications' && (
                <>
                  {myApplications.length > 0 ? (
                    <div className="cch-history-list">
                      {myApplications.map(app => {
                        const club = app.clubs || {};
                        const job = app.job_postings || {};
                        const isPending = app.status === 'pending';
                        const isAccepted = app.status === 'accepted';
                        const isRejected = app.status === 'rejected';
                        const isContacted = app.status === 'contacted';
                        const isRevealed = app.status === 'revealed';
                        return (
                          <div key={app.id} className="cch-history-row" style={{ flexDirection: 'column', gap: 8, padding: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                              <div style={{ width: 40, height: 40, borderRadius: 10, overflow: 'hidden', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {club.logo_url ? (
                                  <img src={club.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <Building2 size={20} color="#94a3b8" />
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong>{job.title || 'Vacante'}</strong>
                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{club.name || 'Club'}</div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {isPending && <span className="cch-badge cch-badge-yellow"><Clock size={12} /> Esperando Respuesta</span>}
                                {isAccepted && <span className="cch-badge cch-badge-green"><CheckCircle size={12} /> Aceptado</span>}
                                {isRejected && <span className="cch-badge cch-badge-red"><XCircle size={12} /> Rechazado</span>}
                                {isContacted && <span className="cch-badge cch-badge-blue"><Mail size={12} /> Quieren contactarte</span>}
                                {isRevealed && <span className="cch-badge cch-badge-green"><CheckCircle size={12} /> Datos revelados</span>}
                              </div>
                            </div>
                            
                            {isRevealed && (
                              <div style={{ background: '#dbeafe', padding: 10, borderRadius: 8, fontSize: '0.85rem' }}>
                                <div><Phone size={12} /> {club.phone || 'Sin teléfono'}</div>
                                <div><Mail size={12} /> {club.email || 'Sin email'}</div>
                              </div>
                            )}
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: 4 }}>
                              <span className="cch-history-date">{new Date(app.applied_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                              <div style={{ display: 'flex', gap: 8 }}>
                                {isContacted && (
                                  <button 
                                    className="cch-btn-sm" 
                                    style={{ background: '#dbeafe', color: '#2563eb' }}
                                    onClick={() => setContactRequestModal({ club, applicationId: app.id })}
                                  >
                                    <Eye size={14} /> Ver Datos de Contacto
                                  </button>
                                )}
                                <button 
                                  className="cch-sidebar-logout" 
                                  style={{ width: 'auto', padding: '6px 12px', fontSize: '0.75rem' }}
                                  onClick={() => handleDeleteApplication(app.id)}
                                  disabled={applicationActionLoading === app.id}
                                >
                                  {applicationActionLoading === app.id ? 'Eliminando...' : 'Eliminar'}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="cch-empty-state">
                      <Send size={48} />
                      <h3>Sin postulaciones</h3>
                      <p>Aquí aparecerán las vacantes a las que te hayas postulado.</p>
                    </div>
                  )}
                </>
)}
            </div>
            </>
          )}

          {/* ============ CLUB CONTACTS ============ */}
          {activePage === 'club-contacts' && (
            <>
              <p className="cch-page-subtitle"><Bell size={16} />Clubes que te han contactado tras revisar tu perfil o postulaciones.</p>
            <div className="cch-card">
              {clubContactsLoading ? (
                <p style={{ textAlign: 'center', padding: 20 }}>Cargando...</p>
              ) : clubContacts.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {clubContacts.map(contact => {
                    const club = contact.clubs || {};
                    const isPending = contact.status === 'pending';
                    const isConfirmed = contact.status === 'confirmed';
                    const dateStr = new Date(contact.requested_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                    return (
                      <div
                        key={contact.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '12px 14px',
                          background: 'white',
                          border: '1px solid #e4e4e7',
                          borderRadius: 10,
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Logo */}
                        <div style={{ width: 40, height: 40, borderRadius: 10, overflow: 'hidden', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {club.logo_url ? (
                            <img src={club.logo_url} alt={club.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Building2 size={18} color="#94a3b8" />
                          )}
                        </div>

                        {/* Info */}
                        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                          <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#18181b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {club.name || 'Club'}
                          </div>
                          <div style={{ fontSize: '0.76rem', color: '#a1a1aa', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {club.location && <span>{club.location}</span>}
                            {club.location && <span>·</span>}
                            <span>{dateStr}</span>
                            {isConfirmed && club.phone && (<><span>·</span><span>{club.phone}</span></>)}
                            {isConfirmed && club.email && (<><span>·</span><span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{club.email}</span></>)}
                          </div>
                        </div>

                        {/* Acción / Estado */}
                        {isPending && (
                          <button
                            onClick={() => handleConfirmContact(contact.id)}
                            disabled={confirmingContact === contact.id}
                            style={{
                              padding: '6px 14px',
                              background: '#8b5cf6',
                              color: 'white',
                              border: 'none',
                              borderRadius: 8,
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {confirmingContact === contact.id ? 'Confirmando...' : 'Confirmar'}
                          </button>
                        )}
                        {isConfirmed && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            color: '#16a34a', fontSize: '0.78rem', fontWeight: 600,
                            flexShrink: 0,
                          }}>
                            <CheckCircle size={14} /> Confirmado
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="cch-empty-state">
                  <Bell size={48} />
                  <h3>Sin contactos</h3>
                  <p>Los clubes que quieran contactarte aparecerán aquí.</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* ============ POSE ANALYSIS ============ */}
          {activePage === 'pose-analysis' && hasPoseAccess && (
            <>
              <p className="cch-page-subtitle">
                <Video size={16} />
                Analiza la postura de tus atletas en tiempo real y graba videos con el esqueleto superpuesto. Todo ocurre en tu dispositivo.
              </p>
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
                padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
                fontSize: '0.82rem', color: '#065f46',
              }}>
                <CheckCircle size={16} />
                <span>
                  Acceso habilitado por: <strong>{poseAnalysisEnabledClubs.map(c => c.name).join(', ')}</strong>
                </span>
              </div>
              <PoseCamera />
            </>
          )}

          {activePage === 'pose-analysis' && !hasPoseAccess && (
            <div style={{
              background: 'white', border: '1px solid #e4e4e7', borderRadius: 14,
              padding: 28, textAlign: 'center',
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: '#fef3c7', color: '#92400e',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12,
              }}>
                <Video size={32} />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#18181b' }}>
                Módulo no disponible
              </h3>
              <p style={{ margin: '0 0 4px', color: '#52525b', fontSize: '0.9rem', maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
                El <strong>Análisis de Pose</strong> es una función premium que habilita cada club para sus entrenadores.
              </p>
              <p style={{ margin: '0 0 20px', color: '#71717a', fontSize: '0.85rem' }}>
                Pide a la administración de tu club que active esta función, o únete a un club que ya la tenga habilitada.
              </p>
              <button
                onClick={() => setActivePage('search-clubs')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', borderRadius: 100, border: 'none',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
                }}
              >
                <Search size={14} /> Buscar clubes
              </button>
            </div>
          )}

          {/* ============ Job Detail Modal ============ */}
          {jobDetailModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setJobDetailModal(null)}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, maxWidth: 450, width: '100%', padding: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Detalles de la Vacante</h3>
                  <button onClick={() => setJobDetailModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ width: 60, height: 60, borderRadius: 12, overflow: 'hidden', margin: '0 auto 12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {jobDetailModal.clubs?.logo_url ? (
                      <img src={jobDetailModal.clubs.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Building2 size={30} color="#94a3b8" />
                    )}
                  </div>
                  <h2 style={{ margin: '0 0 4px', fontSize: '1.15rem' }}>{jobDetailModal.title}</h2>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>{jobDetailModal.clubs?.name}</p>
                  {jobDetailModal.location && <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}><MapPin size={12} /> {jobDetailModal.location}</p>}
                </div>

                {jobDetailModal.description && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 4 }}>Descripción</p>
                    <p style={{ fontSize: '0.9rem', background: '#f8fafc', padding: 10, borderRadius: 8, margin: 0 }}>{jobDetailModal.description}</p>
                  </div>
                )}

                {jobDetailModal.requirements && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 4 }}>Requisitos</p>
                    <p style={{ fontSize: '0.9rem', background: '#f8fafc', padding: 10, borderRadius: 8, margin: 0 }}>{jobDetailModal.requirements}</p>
                  </div>
                )}

                {jobDetailModal.salary_range && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600 }}><Award size={14} /> {jobDetailModal.salary_range}</p>
                  </div>
                )}

                {jobDetailModal.disciplines && jobDetailModal.disciplines.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8 }}>Disciplinas</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {jobDetailModal.disciplines.map((dk, i) => {
                        const d = getDisciplineDisplay(dk);
                        return (
                          <span key={i} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 16, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {d.logo && <img src={d.logo} alt="" style={{ width: 14, height: 14 }} />}
                            {d.fullName}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button className="btn btn-coach-primary" style={{ width: '100%' }} onClick={() => { setApplyingTo(jobDetailModal.id); setJobDetailModal(null); }}>
                  <Send size={16} /> Postularme
                </button>
              </div>
            </div>
          )}

          {/* ============ Contact Request Modal ============ */}
          {contactRequestModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setContactRequestModal(null)}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, maxWidth: 400, width: '100%', padding: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Confirmar</h3>
                  <button onClick={() => setContactRequestModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <Mail size={30} color="#2563eb" />
                  </div>
                  <p style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{contactRequestModal.club?.name}</p>
                  <p style={{ fontSize: '0.9rem', color: '#64748b', margin: 0 }}>
                    Al aceptar, tus datos de contacto serán visibles para este club.
                  </p>
                </div>

                <div style={{ background: '#dbeafe', padding: 12, borderRadius: 8, marginBottom: 20, textAlign: 'center' }}>
                  <p style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0 0 8px' }}>Datos de contacto del club:</p>
                  <div style={{ fontSize: '0.85rem' }}>
                    <div><Phone size={12} /> {contactRequestModal.club?.phone || 'Sin teléfono'}</div>
                    <div><Mail size={12} /> {contactRequestModal.club?.email || 'Sin email'}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button 
                    className="cch-btn-sm" 
                    style={{ flex: 1, background: '#f1f5f9', color: '#475569', justifyContent: 'center' }}
                    onClick={() => setContactRequestModal(null)}
                  >
                    Cancelar
                  </button>
                  <button 
                    className="cch-btn-sm" 
                    style={{ flex: 1, background: '#2563eb', color: 'white', justifyContent: 'center' }}
                    onClick={handleRevealContactInfo}
                    disabled={applicationActionLoading === contactRequestModal.applicationId}
                  >
                    {applicationActionLoading === contactRequestModal.applicationId ? 'Aceptando...' : 'Aceptar'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
        <CategoryFooter />
      </main>
      <IrmaFloatingButton />
    </div>
  );
};

export default CoachDashboard;
